/**
 * LoginPageV2 — Operations Dashboard Style
 * Left: Live KPIs + System Status
 * Center: Branch Selector + Login Form + Quick User Chips
 * Right: Tasks + Login History + Support
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Eye, EyeOff, User, Lock, ChevronRight, Activity,
  Server, CheckCircle, AlertCircle, Clock, Calendar,
  HeadphonesIcon, BarChart2, Users, ShoppingBag, AlertTriangle,
  Building2, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Separator } from '../../components/ui/separator';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useGlobalization } from '../context/GlobalizationContext';
import RetailCliqLogo from '../components/RetailCliqLogo';
import ThemeToggle from '../components/ThemeToggle';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import FloatingParticles from '../components/FloatingParticles';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { API_BASE, API } from '../../lib/api';


const BRANCHES = [
  { id: 1, name: 'Head Office', location: 'Mumbai' },
  { id: 2, name: 'Delhi NCR', location: 'Delhi' },
  { id: 3, name: 'Bangalore', location: 'Bangalore' },
  { id: 4, name: 'Chennai', location: 'Chennai' },
  { id: 5, name: 'Hyderabad', location: 'Hyderabad' },
];

const QUICK_USERS = [
  { name: 'Rajesh K.', initials: 'RK', role: 'Admin', color: '#3DD56D' },
  { name: 'Priya S.', initials: 'PS', role: 'Manager', color: '#4CC3FF' },
  { name: 'Demo', initials: 'DU', role: 'Viewer', color: '#F5C451' },
];

const SYSTEM_STATUS = [
  { name: 'API Server', status: 'up', uptime: '99.9%' },
  { name: 'Database', status: 'up', uptime: '100%' },
  { name: 'File Storage', status: 'up', uptime: '99.7%' },
  { name: 'Email Service', status: 'warning', uptime: '98.2%' },
];

const TASKS = [
  { title: 'Month-end GST filing', due: 'Due: 20th Apr', priority: 'high' },
  { title: 'Inventory audit — Mumbai', due: 'Due: 25th Apr', priority: 'medium' },
  { title: 'Payroll processing', due: 'Due: 25th Apr', priority: 'high' },
  { title: 'Branch performance review', due: 'Due: 30th Apr', priority: 'low' },
];

const RECENT_LOGINS = [
  { device: 'Chrome / Windows', location: 'Mumbai', time: 'Today 09:12 AM' },
  { device: 'Safari / iPhone', location: 'Delhi', time: 'Yesterday 06:30 PM' },
  { device: 'Firefox / Mac', location: 'Bangalore', time: '2 days ago' },
];

function KPICard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="dark:bg-white/[0.04] bg-white dark:border-white/[0.07] border-gray-100 border rounded-xl p-3">
      <div className="flex items-start justify-between mb-1.5">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '20' }}>
          <Icon size={14} style={{ color }} />
        </div>
        {sub && <span className="text-[10px] dark:text-neutral-600 text-gray-400">{sub}</span>}
      </div>
      <p className="text-base font-bold dark:text-white text-gray-900 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{value}</p>
      <p className="text-[10px] dark:text-neutral-500 text-gray-400 mt-0.5">{label}</p>
    </div>
  );
}

export default function LoginPageV2() {
  const { loginWithTemp, completeLogin, accessToken } = useAuth();
  const { isDark } = useTheme();
  const { t, formatTime, formatDate } = useGlobalization();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(BRANCHES[0]);
  const [branchOpen, setBranchOpen] = useState(false);
  const [time, setTime] = useState(new Date());

  useEffect(() => { if (accessToken) navigate('/select-app', { replace: true }); }, [accessToken, navigate]);
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { toast.error('Please enter credentials'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username: username.trim(), password, remember_me: rememberMe, branch_id: selectedBranch.id });
      toast.success(`Welcome, ${data.user_summary.name}!`);
      if (data.requiresMfa) { loginWithTemp(data.temp_token, data.user_summary, rememberMe); navigate('/mfa'); }
      else { completeLogin(data.access_token, data.user_summary, rememberMe); navigate('/select-app'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed.');
    } finally { setLoading(false); }
  }, [username, password, rememberMe, selectedBranch, loginWithTemp, completeLogin, navigate]);

  const quickLogin = (user) => { setUsername(user.name.split('.')[0].toLowerCase()); };

  return (
    <div className="min-h-screen relative overflow-hidden dark:bg-[#080808] bg-slate-50 pb-10" data-testid="login-page-v2">
      {isDark && <FloatingParticles />}

      <div className="relative z-10 min-h-screen grid grid-cols-12">

        {/* LEFT — KPI Dashboard */}
        <div className="hidden lg:flex lg:col-span-3 flex-col p-3 relative overflow-hidden lg:min-h-screen dark:bg-[#0f0f0f] bg-white">
          <div className="absolute top-0 left-0 w-full h-48 pointer-events-none"
            style={{ background: 'radial-gradient(350px circle at 0% 0%, rgba(61,213,109,0.14), transparent 65%)' }} />
          <div className="relative z-10 flex flex-col h-full">
            {/* Logo */}
            <div className="mb-4">
              <RetailCliqLogo size="sm" showTagline />
            </div>

            {/* Date + time */}
            <div className="mb-3 dark:bg-white/[0.03] bg-gray-50 dark:border-white/[0.06] border-gray-100 border rounded-xl p-3">
              <div className="text-2xl font-bold dark:text-white text-gray-900 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                {formatTime(time)}
              </div>
              <div className="dark:text-neutral-500 text-gray-500 text-xs mt-0.5">{formatDate(time, { weekday: 'long' })}</div>
            </div>

            {/* KPI grid */}
            <p className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest mb-2">{t('todayRevenue')} Snapshot</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <KPICard icon={TrendingUp} label={t('todayRevenue')} value="₹12.4L" color="#3DD56D" sub="+8.2%" />
              <KPICard icon={ShoppingBag} label="Active Orders" value="248" color="#4CC3FF" sub="12 new" />
              <KPICard icon={AlertTriangle} label={t('lowStockAlerts')} value="18" color="#F5C451" sub="urgent" />
              <KPICard icon={Users} label="Staff Present" value="42/50" color="#A78BFA" />
            </div>

            {/* System status */}
            <p className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest mb-2">{t('systemStatus')}</p>
            <div className="space-y-1.5 flex-1">
              {SYSTEM_STATUS.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  {s.status === 'up'
                    ? <CheckCircle size={11} className="text-[#3DD56D] flex-shrink-0" />
                    : <AlertCircle size={11} className="text-[#F5C451] flex-shrink-0" />}
                  <span className="dark:text-neutral-300 text-gray-600 text-xs flex-1 truncate">{s.name}</span>
                  <span className="text-[10px] dark:text-neutral-600 text-gray-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{s.uptime}</span>
                </div>
              ))}
            </div>

            <div className="dark:border-t dark:border-white/[0.06] border-t border-gray-100 pt-2 mt-2">
              <p className="dark:text-neutral-600 text-gray-400 text-[10px]">{t('allSystemsNormal')} · v2.4.1</p>
            </div>
          </div>
        </div>

        {/* CENTER — Login Form with Branch + Quick Users */}
        <div className="col-span-12 md:col-span-8 lg:col-span-6 flex items-center justify-center p-3 lg:p-5 relative">
          <div className="absolute top-3 right-3 z-20"><ThemeToggle /></div>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.38 }}
            className="w-full max-w-[420px]">
            <div className="lg:hidden flex justify-center mb-4"><RetailCliqLogo size="sm" showTagline /></div>

            <div className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] shadow-lg rounded-2xl p-5">
              {/* Header */}
              <div className="mb-4">
                <h2 className="text-xl font-bold dark:text-white text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {t('welcomeBack')}
                </h2>
                <p className="dark:text-neutral-400 text-gray-500 text-xs mt-1">{t('signInToAccount')}</p>
              </div>

              {/* Branch selector */}
              <div className="relative mb-3">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400 z-10" size={14} />
                <button
                  type="button"
                  onClick={() => setBranchOpen(v => !v)}
                  className="w-full pl-9 pr-3 h-9 rounded-lg dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 border flex items-center justify-between text-sm dark:text-white text-gray-900 transition-colors dark:hover:bg-white/[0.08] hover:bg-gray-100"
                >
                  <span>{selectedBranch.name}</span>
                  <span className="dark:text-neutral-500 text-gray-400 text-[11px]">{selectedBranch.location}</span>
                </button>
                {branchOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 dark:bg-[#161616] bg-white dark:border-white/[0.1] border-gray-200 border rounded-xl shadow-xl z-30 overflow-hidden">
                    {BRANCHES.map(b => (
                      <button key={b.id} onClick={() => { setSelectedBranch(b); setBranchOpen(false); }}
                        className="w-full flex items-center justify-between px-3 py-2 dark:hover:bg-white/[0.07] hover:bg-gray-50 transition-colors">
                        <span className="text-xs dark:text-white text-gray-900 font-medium">{b.name}</span>
                        <span className="text-[11px] dark:text-neutral-500 text-gray-400">{b.location}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick user chips */}
              <div className="mb-3">
                <p className="text-[10px] dark:text-neutral-500 text-gray-400 mb-1.5">Quick select:</p>
                <div className="flex gap-2">
                  {QUICK_USERS.map((u, i) => (
                    <button key={i} onClick={() => quickLogin(u)}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-lg dark:bg-white/[0.04] bg-gray-100 dark:border-white/[0.07] border-gray-200 border dark:hover:bg-white/[0.08] hover:bg-gray-200 transition-colors">
                      <div className="w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-bold text-black flex-shrink-0" style={{ backgroundColor: u.color }}>
                        {u.initials}
                      </div>
                      <div className="text-left">
                        <p className="text-[10px] dark:text-white text-gray-900 font-medium leading-none">{u.name}</p>
                        <p className="text-[9px] dark:text-neutral-500 text-gray-400 leading-none mt-0.5">{u.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form */}
              <FormHelp formName="Login">
              <form onSubmit={handleLogin} className="space-y-2.5" data-dev-component="LoginPortalV2">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={14} />
                  <Input data-testid="login-username-input" data-help-control="username" type="text" placeholder={t('usernamePlaceholder')} value={username}
                    onChange={e => setUsername(e.target.value)} autoComplete="username"
                    className="pl-9 h-9 rounded-lg dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-[#3DD56D] focus-visible:ring-1 text-sm" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={14} />
                  <Input data-testid="login-password-input" data-help-control="password" type={showPassword ? 'text' : 'password'} placeholder={t('passwordPlaceholder')}
                    value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
                    className="pl-9 pr-9 h-9 rounded-lg dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-[#3DD56D] focus-visible:ring-1 text-sm" />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400 hover:text-gray-600 transition-colors">
                    {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Checkbox id="rm2" data-help-control="remember_me" checked={rememberMe} onCheckedChange={v => setRememberMe(!!v)}
                      className="dark:border-white/[0.2] border-gray-300 data-[state=checked]:bg-[#3DD56D] data-[state=checked]:border-[#3DD56D] h-3.5 w-3.5" />
                    <label htmlFor="rm2" className="text-xs dark:text-neutral-400 text-gray-500 cursor-pointer">{t('rememberMe')}</label>
                  </div>
                  <button type="button" onClick={() => setShowForgot(true)}
                    className="text-xs text-[#3DD56D] hover:text-[#5fe88a] transition-colors">{t('forgotPassword')}</button>
                </div>
                <Button type="submit" data-testid="login-submit-button" disabled={loading}
                  className="w-full h-9 rounded-lg bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold shadow-[0_4px_16px_rgba(61,213,109,0.22)] disabled:opacity-50 text-sm">
                  {loading
                    ? <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />{t('signingIn')}</span>
                    : <span className="flex items-center gap-1.5">{t('signInBtn')} <ChevronRight size={14} /></span>}
                </Button>
              </form>
              </FormHelp>

              <Separator className="dark:bg-white/[0.07] bg-gray-100 my-3" />
              {/* "Request Access" hidden per product request
              <p className="text-center text-xs dark:text-neutral-500 text-gray-500">
                {t('newTo')}{' '}
                <a href="/signup" className="text-[#3DD56D] hover:text-[#5fe88a] font-medium">{t('requestAccess')}</a>
              </p>
              */}
            </div>

            {/* Demo hint */}
            <div className="mt-2 dark:bg-[#3DD56D]/[0.04] bg-[#3DD56D]/[0.04] border dark:border-[#3DD56D]/15 border-[#3DD56D]/15 rounded-lg px-3 py-2">
              <p className="text-[11px] dark:text-neutral-400 text-gray-500">
                <strong>Demo:</strong> admin@retailcliq.com / Admin@123 · demo / demo123
              </p>
            </div>
          </motion.div>
        </div>

        {/* RIGHT — Tasks + Login History + Support */}
        <div className="hidden md:flex md:col-span-4 lg:col-span-3 flex-col p-3 relative overflow-hidden lg:min-h-screen dark:bg-[#0c0c0c] bg-slate-50">
          <div className="absolute bottom-0 right-0 w-full h-48 pointer-events-none"
            style={{ background: 'radial-gradient(280px circle at 110% 110%, rgba(61,213,109,0.08), transparent 60%)' }} />
          <div className="relative z-10 flex flex-col h-full gap-4">

            {/* Upcoming tasks */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Calendar size={11} className="text-[#3DD56D]" />
                <p className="text-[10px] font-semibold dark:text-neutral-400 text-gray-500 uppercase tracking-widest">Upcoming Tasks</p>
              </div>
              <div className="space-y-1.5">
                {TASKS.map((task, i) => (
                  <div key={i} className="dark:bg-white/[0.03] bg-white dark:border-white/[0.06] border-gray-100 border rounded-lg px-2.5 py-2">
                    <div className="flex items-start gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${task.priority === 'high' ? 'bg-[#FF5A6A]' : task.priority === 'medium' ? 'bg-[#F5C451]' : 'bg-[#3DD56D]'}`} />
                      <div>
                        <p className="text-[11px] dark:text-neutral-200 text-gray-700 font-medium leading-snug">{task.title}</p>
                        <p className="text-[10px] dark:text-neutral-600 text-gray-400">{task.due}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent logins */}
            <div className="flex-1">
              <div className="flex items-center gap-1.5 mb-2">
                <Activity size={11} className="dark:text-neutral-500 text-gray-400" />
                <p className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest">{t('recentLogins')}</p>
              </div>
              <div className="space-y-1.5">
                {RECENT_LOGINS.map((login, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-[#3DD56D]/50 mt-1.5 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] dark:text-neutral-300 text-gray-600">{login.device}</p>
                      <p className="text-[10px] dark:text-neutral-600 text-gray-400">{login.time} · {login.location}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Support */}
            <div className="dark:border-t dark:border-white/[0.06] border-t border-gray-100 pt-2">
              <div className="flex items-center gap-1.5 mb-1.5">
                <HeadphonesIcon size={11} className="dark:text-neutral-500 text-gray-400" />
                <p className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest">{t('contactSupport')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[11px] dark:text-neutral-400 text-gray-500">support@retailcliq.com</p>
                <p className="text-[11px] dark:text-neutral-400 text-gray-500">+91 1800-RCQ-HELP</p>
              </div>
              <p className="dark:text-neutral-700 text-gray-400 text-[10px] mt-2">{t('versionInfo')}: v2.4.1 · 2025 RetailCliQ</p>
            </div>
          </div>
        </div>

      </div>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}
